// Layer ("system") definitions and anatomy colours (F02/F21). Framework-free.

export type LayerId = 'skin' | 'skeletal' | 'muscular' | 'organs' | 'nervous' | 'vascular' | 'ligaments';
export type LayerState = 'on' | 'ghost' | 'off';

export interface LayerDef {
  id: LayerId;
  label: string;
  short: string; // URL code
  color: string;
  /** Default state on a fresh load. */
  initial: LayerState;
  /** Outside-in peel order: lower = outer. */
  depth: number;
  /** Load order: lower loads first. */
  loadOrder: number;
}

export const LAYERS: LayerDef[] = [
  { id: 'skin', label: 'Skin', short: 'sn', color: '#e8c9b0', initial: 'ghost', depth: 0, loadOrder: 0 },
  { id: 'muscular', label: 'Muscles', short: 'mu', color: '#c2554b', initial: 'on', depth: 1, loadOrder: 3 },
  { id: 'organs', label: 'Organs', short: 'or', color: '#d9a07a', initial: 'on', depth: 2, loadOrder: 2 },
  { id: 'vascular', label: 'Heart & vessels', short: 'va', color: '#c9302c', initial: 'on', depth: 3, loadOrder: 4 },
  { id: 'nervous', label: 'Brain & nerves', short: 'ne', color: '#e8c547', initial: 'on', depth: 3, loadOrder: 5 },
  { id: 'ligaments', label: 'Ligaments & joints', short: 'li', color: '#9fb8ad', initial: 'off', depth: 4, loadOrder: 6 },
  { id: 'skeletal', label: 'Skeleton', short: 'sk', color: '#efe6d2', initial: 'on', depth: 5, loadOrder: 1 },
];

export const LAYER_BY_ID = Object.fromEntries(LAYERS.map((l) => [l.id, l])) as Record<LayerId, LayerDef>;
export const PEEL_ORDER: LayerId[] = ['skin', 'muscular', 'organs', 'vascular', 'nervous', 'ligaments'];

export function initialLayers(): Record<LayerId, LayerState> {
  return Object.fromEntries(LAYERS.map((l) => [l.id, l.initial])) as Record<LayerId, LayerState>;
}

/** Structure-specific colours so organs and vessels read naturally. First match wins. */
const COLOR_RULES: Array<[RegExp, string]> = [
  [/vein|venous|sinus|vena-cava|azygos|pulmonary-arteries|cardiac-veins/, '#3e6fb0'],
  [/pulmonary-veins/, '#c9302c'],
  [/^heart$/, '#b8423c'],
  [/liver/, '#8e3b2e'],
  [/lungs|bronchi|trachea/, '#e3a7a1'],
  [/kidney|spleen|adrenal/, '#9a4038'],
  [/stomach|small-intestine|duodenum|colon|caecum|appendix|oesophagus|pharynx|rectum/, '#e0a58f'],
  [/thymus|thyroid|parathyroid|pituitary|pineal/, '#c98b6b'],
  [/lymph-nodes|tonsils/, '#9cc28f'],
  [/lobe$|cerebral-white-matter|insula|cerebellum|basal-ganglia|hippocampus|amygdala|thalamus|hypothalamus|midbrain|pons|medulla-oblongata|spinal-cord/, '#e7c2b8'],
  [/^eye$/, '#f2f2ee'],
  [/teeth/, '#f6f1e1'],
  [/cartilage|intervertebral-discs|menisci/, '#cfe0e3'],
  [/tendon|calcaneal-tendon|iliotibial|aponeurosis/, '#e9dfc8'],
];

export function colorFor(id: string, layer: LayerId): string {
  for (const [re, c] of COLOR_RULES) if (re.test(id)) return c;
  return LAYER_BY_ID[layer]?.color ?? '#cccccc';
}
